import { useQuery } from "@apollo/client";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { Button, Stack } from "@mui/material";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { StatCard, StatCardRowSkeleton } from "components/StatCard";
import { VolumeStatus, WasteAccumulationAreaType } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import pluralize from "pluralize";
import { Link as RouterLink } from "react-router-dom";

import { FACILITY_ACCUMULATION_AREAS_TILE_QUERY } from "../../AccumulationAreas/api";
import { StatCardGrid } from "../../components/StatCardGrid";
import { StatSubstats } from "../../components/StatSubstats";
import { useWasteFacilityState } from "./WasteFacilityContext";

/**
 * Accumulation areas summary for the waste facility dashboard: the area count
 * with its satellite/central split, how many are over their volume threshold,
 * and a link to the facility-scoped areas page.
 *
 * Always half-width — see `ContainerAccumulationAndEpisodicTiles` in
 * `WasteFacility` for how it and its row-mates size themselves depending on
 * whether the episodic-events tile is on the dashboard.
 *
 * Why "over threshold" and not "approaching deadline": a satellite area has no
 * deadline until it is ALREADY over its limit — crossing the threshold is what
 * starts the three-day clock to move the waste to central. So an
 * approaching-deadline count here would be a subset of the over-threshold
 * count, reporting one problem twice, and the container-level deadline clock is
 * already summarized on the Container Tracking tile. The figure that is
 * genuinely additional is the area that is filling up but has NOT yet tripped —
 * `VolumeStatus.Approaching` — so that is the substat.
 */
export const WasteAccumulationAreasTile = () => {
  const { tenantId } = useTenant();
  const { effectiveWasteFacilityId } = useWasteFacilityState();

  const { data, loading } = useQuery(FACILITY_ACCUMULATION_AREAS_TILE_QUERY, {
    variables: { facilityId: effectiveWasteFacilityId },
    skip: !effectiveWasteFacilityId,
  });
  // Every figure on this tile is derived from `items`, including the headline
  // area count: the resolver's `count` is a `count()` over the same unpaginated
  // `where` in the same transaction as the `findMany`, so the two can only
  // agree — and reading one list keeps that visible rather than implied.
  const loadedAreas = data?.facility?.accumulationAreas?.items;
  const items = loadedAreas ?? [];
  const byStatus = (status: VolumeStatus) =>
    items.filter((area) => area.volume.status === status).length;
  const overThresholdCount = byStatus(VolumeStatus.Over);
  const approachingCount = byStatus(VolumeStatus.Approaching);
  const satelliteCount = items.filter(
    (area) => area.type === WasteAccumulationAreaType.Satellite
  ).length;
  const centralCount = items.filter(
    (area) => area.type === WasteAccumulationAreaType.Central
  ).length;

  return (
    <DashboardTile size={{ xs: 12, md: 6 }}>
      <DashboardTile.Header title="Accumulation Areas" />
      <DashboardTile.ContentArea>
        <Stack spacing={2} alignItems="stretch" sx={{ width: "100%" }}>
          {loading && loadedAreas == null ? (
            <StatCardRowSkeleton count={2} />
          ) : (
            <StatCardGrid>
              <StatCard
                captionPlacement="top"
                caption={pluralize("Area", items.length)}
                value={String(items.length)}
                subCaption={`${satelliteCount} satellite · ${centralCount} central`}
              />
              <StatCard
                captionPlacement="top"
                caption="Over threshold"
                value={String(overThresholdCount)}
                valueColor={overThresholdCount > 0 ? "error.main" : "text.primary"}
                footer={
                  items.length > 0 ? (
                    <StatSubstats
                      // Only claim all-clear when the headline agrees: with
                      // areas already over, "all within threshold" would
                      // contradict the number directly above it.
                      allClearLabel={overThresholdCount === 0 ? "All within threshold" : undefined}
                      substats={[
                        {
                          count: approachingCount,
                          label: "approaching limit",
                          color: "warning.main",
                        },
                      ]}
                    />
                  ) : undefined
                }
              />
            </StatCardGrid>
          )}
          <Button
            component={RouterLink}
            to={`/o/${tenantId}/waste/accumulation-areas?facilityId=${effectiveWasteFacilityId}`}
            variant="text"
            size="small"
            endIcon={<ArrowForward />}
            sx={{ alignSelf: "flex-start" }}
          >
            View areas
          </Button>
        </Stack>
      </DashboardTile.ContentArea>
    </DashboardTile>
  );
};
